import { useState } from 'react';
import { PackageCheckIcon, Undo2Icon } from 'lucide-react';
import {
  RewardRedemptionStatus,
  adminCancelRewardRedemptionRequestSchema,
  adminDeliverRewardRedemptionRequestSchema,
  type AdminRewardRedemptionFilter,
  type AdminRewardRedemptionRow,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { PageHeader } from '@/components/page-header';
import { PagerControls } from '@/components/pager';
import { StatCard } from '@/components/stat-card';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import { useAdminForm } from '@/lib/admin-form';
import {
  useCancelRedemption,
  useDeliverRedemption,
  useRewardRedemptions,
  useRewardRedemptionStats,
} from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { strings } from '@/i18n/strings';

const COLUMNS = 6;

const STATUS_LABEL: Record<string, string> = {
  [RewardRedemptionStatus.PENDING]: strings.redemptions.statusPending,
  [RewardRedemptionStatus.DELIVERED]: strings.redemptions.statusDelivered,
  [RewardRedemptionStatus.CANCELLED]: strings.redemptions.statusCancelled,
};

function moment(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('uz-UZ', { timeZone: 'Asia/Tashkent' }) : '—';
}

/**
 * Sovg'a buyurtmalari navbati (P8.1).
 *
 * Sahifa bitta savolga javob beradi: KIMGA nima topshirilishi kerak. Shuning uchun u
 * `pending` bilan ochiladi — bo'sh filtr ishni allaqachon topshirilganlar ostiga ko'mib
 * qo'yardi, va o'sha ro'yxat vaqt o'tgani sari faqat o'sadi.
 *
 * Ikkita tugma bor va ular teng emas: "Topshirildi" — jismoniy voqeaning qaydi, ballga
 * tegmaydi (u almashtirish paytida allaqachon yechilgan). "Bekor qilish" esa BALLNI
 * QAYTARADI, ya'ni pul harakati — shu sababli u sababsiz bosilmaydi va topshirilgan
 * buyurtmadan ham ochiq qoladi (xato yetkazish faqat keyin bilinadi).
 */
export function RewardRedemptionsPage() {
  const [filter, setFilter] = useState<AdminRewardRedemptionFilter>({
    status: RewardRedemptionStatus.PENDING,
  });
  const pager = useOffsetPager();

  const stats = useRewardRedemptionStats();
  const list = useRewardRedemptions({ ...filter, page: pager.page, limit: pager.limit });

  /** Har bir filtr o'zgarishi birinchi sahifaga qaytaradi — aks holda bo'sh sahifa ochilardi. */
  function update(patch: Partial<AdminRewardRedemptionFilter>) {
    setFilter((current) => ({ ...current, ...patch }));
    pager.reset();
  }

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title={strings.redemptions.title} description={strings.redemptions.subtitle} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={strings.redemptions.statPending} value={stats.data?.pending ?? '—'} />
        <StatCard label={strings.redemptions.statDelivered} value={stats.data?.delivered ?? '—'} />
        <StatCard label={strings.redemptions.statCancelled} value={stats.data?.cancelled ?? '—'} />
        {/*
          Ochiq majburiyat: topshirilmagan sovg'alar uchun ALLAQACHON yechilgan ball. U
          balansda ham, katalogda ham ko'rinmaydi — faqat shu yerda.
        */}
        <StatCard
          label={strings.redemptions.statPendingPoints}
          value={stats.data ? formatNumber(stats.data.pendingPoints) : '—'}
          hint={strings.redemptions.statPendingPointsHint}
        />
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-5">
          <Field label={strings.redemptions.filterStatus}>
            <Select
              value={filter.status ?? ''}
              onChange={(event) =>
                update({
                  status: (event.target.value ||
                    undefined) as AdminRewardRedemptionFilter['status'],
                })
              }
            >
              <option value="">{strings.redemptions.all}</option>
              {Object.entries(STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={strings.redemptions.filterQuery} hint={strings.redemptions.filterQueryHint}>
            <Input
              value={filter.query ?? ''}
              onChange={(event) => update({ query: event.target.value || undefined })}
            />
          </Field>

          <Field label={strings.redemptions.filterFrom}>
            <Input
              type="date"
              value={filter.from ?? ''}
              onChange={(event) => update({ from: event.target.value || undefined })}
            />
          </Field>

          <Field label={strings.redemptions.filterTo}>
            <Input
              type="date"
              value={filter.to ?? ''}
              onChange={(event) => update({ to: event.target.value || undefined })}
            />
          </Field>

          <div className="flex flex-col justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilter({});
                pager.reset();
              }}
            >
              {strings.redemptions.filterReset}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{strings.redemptions.title}</CardTitle>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <TableSkeleton rows={8} columns={COLUMNS} />
          ) : list.isError ? (
            <ErrorNotice error={list.error} />
          ) : rows.length === 0 ? (
            <EmptyState title={strings.redemptions.empty} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{strings.redemptions.columnUser}</TableHead>
                    <TableHead>{strings.redemptions.columnItem}</TableHead>
                    <TableHead>{strings.redemptions.columnPoints}</TableHead>
                    <TableHead>{strings.redemptions.columnStatus}</TableHead>
                    <TableHead>{strings.redemptions.columnDecision}</TableHead>
                    <TableHead>{strings.redemptions.columnActions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="text-sm">{row.displayName || '—'}</div>
                        {/* Yetkazish uchun yagona aloqa kanali — buyurtma paytidagi nusxasi. */}
                        <div className="font-mono text-xs">{row.phoneNumber ?? '—'}</div>
                        <div className="text-muted-foreground font-mono text-xs">
                          {row.telegramId}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{row.itemName}</TableCell>
                      <TableCell className="font-mono text-sm whitespace-nowrap">
                        {formatNumber(row.pointsSpent)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant={statusVariant(row.status)}>
                            {STATUS_LABEL[row.status] ?? row.status}
                          </Badge>
                          <span className="text-muted-foreground text-xs whitespace-nowrap">
                            {moment(row.createdAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <Decision row={row} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <DeliverAction row={row} />
                          <CancelAction row={row} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <PagerControls
            pager={pager}
            total={list.data?.total}
            pageCount={list.data?.pageCount}
            rowCount={rows.length}
            busy={list.isFetching}
            label={strings.redemptions.title}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' {
  if (status === RewardRedemptionStatus.DELIVERED) return 'default';
  if (status === RewardRedemptionStatus.CANCELLED) return 'destructive';
  return 'secondary';
}

/** Kim, qachon va nima deb qaror qildi. `pending` da — hali hech kim. */
function Decision({ row }: { row: AdminRewardRedemptionRow }) {
  if (row.status === RewardRedemptionStatus.PENDING) {
    return <span className="text-muted-foreground text-xs">{strings.redemptions.noDecision}</span>;
  }
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="text-xs whitespace-nowrap">{moment(row.decidedAt)}</div>
      <div className="text-muted-foreground text-xs break-words">{row.decidedByEmail ?? '—'}</div>
      {row.adminNote && (
        <div className="text-xs break-words" title={row.adminNote}>
          {row.adminNote}
        </div>
      )}
    </div>
  );
}

/**
 * "Topshirildi" — faqat `pending` dan.
 *
 * Bekor qilingan buyurtmani topshirish uchun ball qaytadan yechilishi kerak bo'lardi, va bu
 * bu tugma qiladigan ish emas: o'shanda foydalanuvchi yangi buyurtma beradi.
 */
function DeliverAction({ row }: { row: AdminRewardRedemptionRow }) {
  const [open, setOpen] = useState(false);
  const mutation = useDeliverRedemption();
  const form = useAdminForm({
    schema: adminDeliverRewardRedemptionRequestSchema,
    defaultValues: { note: '' },
  });

  if (row.status !== RewardRedemptionStatus.PENDING) return null;

  const submit = form.handleSubmit((values) => {
    mutation.mutate(
      { id: row.id, ...(values.note ? { note: values.note } : {}) },
      { onSuccess: () => setOpen(false), onError: form.applyServerError },
    );
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset({ note: '' });
      }}
      trigger={
        <Button variant="outline" size="sm">
          <PackageCheckIcon aria-hidden="true" />
          {strings.redemptions.deliver}
        </Button>
      }
      title={strings.redemptions.deliverTitle}
      description={strings.redemptions.deliverHint}
      submitLabel={strings.redemptions.deliverSubmit}
      submitting={mutation.isPending}
      onSubmit={submit}
      error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
    >
      <Field
        label={strings.redemptions.note}
        hint={strings.redemptions.noteHint}
        error={form.formState.errors.note?.message}
      >
        <Input {...form.register('note')} />
      </Field>
    </FormDialog>
  );
}

/**
 * "Bekor qilish + ball qaytarish".
 *
 * `delivered` dan ham ochiq: xato yetkazish (noto'g'ri odam, omborda yo'q narsa) faqat
 * topshirilgan deb belgilangandan KEYIN bilinadi. Sabab majburiy — bu ledgerga musbat qator
 * yozadigan tugma, va qatorning o'zi "nega" degan savolga javob bermaydi.
 */
function CancelAction({ row }: { row: AdminRewardRedemptionRow }) {
  const [open, setOpen] = useState(false);
  const mutation = useCancelRedemption();
  const form = useAdminForm({
    schema: adminCancelRewardRedemptionRequestSchema,
    defaultValues: { reason: '' },
  });

  if (row.status === RewardRedemptionStatus.CANCELLED) return null;

  const submit = form.handleSubmit((values) => {
    mutation.mutate(
      { id: row.id, reason: values.reason },
      { onSuccess: () => setOpen(false), onError: form.applyServerError },
    );
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset({ reason: '' });
      }}
      trigger={
        <Button variant="destructive" size="sm">
          <Undo2Icon aria-hidden="true" />
          {strings.redemptions.cancel}
        </Button>
      }
      title={strings.redemptions.cancelTitle}
      description={`${strings.redemptions.cancelHint} — ${formatNumber(row.pointsSpent)}`}
      submitLabel={strings.redemptions.cancelSubmit}
      submitting={mutation.isPending}
      onSubmit={submit}
      error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
    >
      <Field
        label={strings.redemptions.reason}
        hint={strings.redemptions.reasonHint}
        error={form.formState.errors.reason?.message}
      >
        <Input {...form.register('reason')} />
      </Field>
    </FormDialog>
  );
}
